import { ExclamationTriangle } from '@squareup/dex-icons/market/alert';
import { ApplicationType } from '@squareup/dex-types-shared-application-type';
import { Box, Paragraph10 } from '@squareup/dex-ui-shared-base';
import {
  MarketSegmentedControl,
  MarketSegment,
  MarketTooltip,
} from '@squareup/dex-ui-shared-market';
import clsx from 'clsx';
import React, { FC, useMemo, useState } from 'react';

import styles from './sandbox-toggle.module.css';

const strings = {
  sandbox: 'Sandbox',
  production: 'Production',
  sandboxNotSupported: 'Sandbox is not supported for this endpoint.',
  prodNotSupported: 'No access to production tokens for this account.',
};

const WarningIcon: FC = () => (
  <ExclamationTriangle
    className={styles['warning-icon']}
    height={12}
    width={12}
    viewBox="0 0 24 24"
  />
);

type TooltipProps = {
  show: boolean;
  text: string;
};

const Tooltip: FC<TooltipProps> = ({ show, text }) => (
  // DPW-774: Hovering MarketTooltip and leaving it causes the access token dropdown to close
  <MarketTooltip
    expanded={show}
    className={styles['tooltip'] as string}
    popoverDistance={20}
  >
    <Paragraph10 as="span" slot="content">
      {text}
    </Paragraph10>
  </MarketTooltip>
);

type Props = {
  onChange: (value: ApplicationType) => void;
  supportedEnvironments?: ApplicationType[];
  value: ApplicationType;
};

const SandboxToggle: FC<Props> = ({
  onChange,
  supportedEnvironments,
  value,
}) => {
  const [envHovered, setEnvHovered] = useState<ApplicationType>();

  const isSandboxSupported = useMemo(
    () =>
      supportedEnvironments
        ? supportedEnvironments.includes(ApplicationType.Sandbox)
        : true,
    [supportedEnvironments]
  );

  return (
    <Box className={styles['sandbox-toggle']}>
      <MarketSegmentedControl
        className={styles['sandbox-toggle-control'] as string}
        data-testid="toggle-control"
        value={value}
        onMarketSegmentedSelectionDidChange={({
          detail: { selectedSegmentValue },
        }) => onChange(selectedSegmentValue as ApplicationType)}
      >
        <MarketSegment
          className={clsx(styles['segment'], styles['segment-sandbox'])}
          data-testid="toggle-segment-sandbox"
          disabled={!isSandboxSupported}
          {...(!isSandboxSupported && {
            onMouseEnter: () => setEnvHovered(ApplicationType.Sandbox),
            onMouseLeave: () => setEnvHovered(undefined),
          })}
          value={ApplicationType.Sandbox}
        >
          {!isSandboxSupported && (
            <>
              <WarningIcon />
              <Tooltip
                show={envHovered === ApplicationType.Sandbox}
                text={strings['sandboxNotSupported']}
              />
            </>
          )}
          {strings['sandbox']}
        </MarketSegment>
        <MarketSegment
          className={clsx(styles['segment'], styles['segment-production'])}
          data-testid="toggle-segment-production"
          value={ApplicationType.Production}
        >
          {strings['production']}
        </MarketSegment>
      </MarketSegmentedControl>
    </Box>
  );
};

export { SandboxToggle };
